import { Reveal, RevealLine } from "@/components/Reveal";
import { Placeholder, IMAGES } from "@/components/Placeholder";
import { Sparkles, BadgeIndianRupee, TrendingUp, Layers, Package, Settings2 } from "lucide-react";

const OCCASIONS = [
  "Weddings",
  "Diwali",
  "Housewarmings",
  "Corporate Gifting",
  "Spiritual Décor",
  "Hospitality",
  "Luxury Home Styling",
];

const ADV = [
  { icon: Sparkles, title: "Exclusive Category", body: "A defensible niche with no global incumbent." },
  { icon: BadgeIndianRupee, title: "Wide Price Range", body: "From accessible gifting to high luxury." },
  { icon: TrendingUp, title: "Strong Margins", body: "Designed for premium retail economics." },
  { icon: Layers, title: "2200+ SKUs", body: "Deep, ever-evolving catalogue across collections." },
  { icon: Package, title: "Premium Packaging", body: "Each piece arrives as a gifting moment." },
];

export default function Experience() {
  return (
    <section
      id="experience"
      data-testid="section-experience"
      className="deck-section relative bg-ink overflow-hidden"
    >
      {/* Background lifestyle image with strong overlay */}
      <div className="absolute inset-0">
        <Placeholder
          src={IMAGES.glassware}
          alt="Designed for every occasion — Aarya24KT"
          aspect="aspect-auto h-full"
          className="h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/50 to-ink/10" />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-[1600px] flex-col justify-between min-h-screen px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <Reveal>
          <div className="num-overline">06 · Experience & Advantage</div>
        </Reveal>

        {/* Customisation — the hero callout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start mt-8">
          {/* LEFT — Customisation hero + occasions */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <h2 className="font-heading text-ivory text-4xl md:text-5xl lg:text-[64px] leading-[1.02] tracking-tightest">
                Designed for every occasion.
                <br />
                <span className="italic text-gold">Customised</span> for every retailer.
              </h2>
            </Reveal>

            <Reveal delay={0.25}>
              <div className="mt-10 flex items-start gap-6 border border-gold/30 bg-coal/40 backdrop-blur-sm p-6 md:p-8 max-w-2xl">
                <Settings2 className="h-6 w-6 flex-none text-gold" strokeWidth={1.25} />
                <div>
                  <div className="num-overline text-gold/90">Signature Capability</div>
                  <div className="mt-2 font-heading text-ivory text-2xl md:text-3xl tracking-tight">
                    Bespoke <span className="italic text-gold">Customisation</span>
                  </div>
                  <p className="mt-3 font-body text-[14px] leading-[1.75] text-beige/80 max-w-md">
                    Private label, festive editions, corporate gifts and bespoke
                    commissions — tailored to every retailer's market, festival
                    calendar and clientele.
                  </p>
                </div>
              </div>
            </Reveal>

            {/* Occasion tags */}
            <Reveal delay={0.4}>
              <div className="mt-12">
                <div className="num-overline text-ivory/60">Built for these moments</div>
                <div className="mt-5 flex flex-wrap gap-x-7 gap-y-3 max-w-3xl">
                  {OCCASIONS.map((o, i) => (
                    <div key={i} className="flex items-baseline gap-3">
                      <span className="num-overline text-gold/70">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-heading italic text-ivory text-lg md:text-xl">
                        {o}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          {/* RIGHT — Retail Advantage minimal list */}
          <div className="lg:col-span-5 lg:pt-8">
            <Reveal delay={0.15}>
              <div className="num-overline text-gold/80">The Retail Advantage</div>
              <h3 className="mt-3 font-heading text-ivory text-2xl md:text-3xl tracking-tight">
                Engineered for sell-through.
              </h3>
              <RevealLine delay={0.3} className="mt-6 w-16" />
            </Reveal>

            <ul className="mt-8 space-y-5">
              {ADV.map((a, i) => {
                const Icon = a.icon;
                return (
                  <Reveal key={i} delay={0.25 + i * 0.06}>
                    <li className="group flex items-start gap-5 border-b border-gold/12 pb-5">
                      <Icon className="h-4 w-4 flex-none text-gold mt-1.5" strokeWidth={1.25} />
                      <div>
                        <div className="font-heading text-ivory text-lg md:text-xl leading-tight">
                          {a.title}
                        </div>
                        <div className="mt-1 font-body text-[13px] leading-[1.6] text-beige/65">
                          {a.body}
                        </div>
                      </div>
                    </li>
                  </Reveal>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
